import { useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SEOVerificationMeta — Fetches admin-configured Google + Bing verification codes
 * and Google Analytics 4 ID from the backend (/api/seo/public-meta), then injects
 * them into <head> at runtime.
 *
 * Why runtime injection?
 * - Google Search Console + Bing Webmaster Tools "live test" the meta tag with a
 *   real headless browser that executes JS, so this works for verification.
 * - Crawlers also see the meta tag because the SSR endpoint /api/seo/render injects
 *   them at the source, AND because Googlebot evaluates JS.
 * - This way the admin can update verification codes without redeploying.
 *
 * Mount once at the top of the App component.
 */
export default function SEOVerificationMeta() {
  useEffect(() => {
    let mounted = true;
    const cache = sessionStorage.getItem("seo_public_meta");
    if (cache) {
      try {
        applyMeta(JSON.parse(cache));
      } catch (_e) {
        /* ignore */
      }
    }
    axios
      .get(`${API_URL}/api/seo/public-meta`, { timeout: 5000 })
      .then((r) => {
        if (!mounted) return;
        sessionStorage.setItem("seo_public_meta", JSON.stringify(r.data));
        applyMeta(r.data);
      })
      .catch(() => {
        /* silently ignore — non-critical */
      });
    return () => {
      mounted = false;
    };
  }, []);

  return null;
}

function applyMeta({ google_verification, bing_verification, ga4_measurement_id }) {
  ensureMeta("google-site-verification", google_verification);
  ensureMeta("msvalidate.01", bing_verification);
  ensureGA4(ga4_measurement_id);
}

function ensureMeta(name, content) {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  if (el.getAttribute("content") !== content) {
    el.setAttribute("content", content);
  }
}

function ensureGA4(id) {
  if (!id || !id.startsWith("G-")) return;
  if (document.getElementById("ga4-script")) return;

  const s1 = document.createElement("script");
  s1.id = "ga4-script";
  s1.async = true;
  s1.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s1);

  const s2 = document.createElement("script");
  s2.text = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${id}', { anonymize_ip: true });`;
  document.head.appendChild(s2);
}
